// Central helper for the "currently selected city" the user is browsing.
// Persisted in localStorage so it survives navigation and page reloads.
// The rule: if the user has picked a city manually, we NEVER overwrite it
// with auto-geolocation. Only auto-detect when the stored value is empty.

const KEY = 'bookvia-selected-city';
const EVT = 'bookvia:city-changed';

export function getSelectedCity() {
  try {
    return localStorage.getItem(KEY) || '';
  } catch {
    return '';
  }
}

export function setSelectedCity(city) {
  try {
    const val = (city || '').toString().trim();
    if (val) localStorage.setItem(KEY, val);
    else localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(EVT, { detail: { city: val } }));
  } catch { /* ignore */ }
}

export function clearSelectedCity() {
  setSelectedCity('');
}

// React hook: keep a component's state in sync with the global selected city.
// Multiple screens (Home, Search, Categories) subscribe so a change on one
// screen instantly reflects on every other mounted screen.
import { useEffect, useState, useCallback } from 'react';

export function useSelectedCity() {
  const [city, setCityState] = useState(() => getSelectedCity());

  useEffect(() => {
    const onChange = (e) => setCityState(e?.detail?.city || '');
    const onStorage = (e) => {
      if (e.key === KEY) setCityState(e.newValue || '');
    };
    window.addEventListener(EVT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setCity = useCallback((v) => {
    setSelectedCity(v);
    setCityState((v || '').toString().trim());
  }, []);

  return [city, setCity];
}
